import { COMPILED_COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import { decodeBase64, encodeBase64 } from "../document/base64.ts";
import { asRecord, asString } from "../document/validate.ts";
import { STAND_BEHIND, STAND_FRONT, type StandTable } from "./stand-marks.ts";

/**
 * The stand table of a client chunk (ADR-132 decision 5): the marked cells of the chunk with the side and the depth
 * of the prop that decides each, in the compiled binary layout. The three arrays are parallel and sorted by cell, and
 * each is written as base64 of its little-endian bytes, as the chunk's other typed arrays are. A cell is its index in
 * the chunk's grid of compiled collision cells, row-major.
 */

/**
 * The marked cells of one chunk.
 */
export interface ChunkStand {
  readonly cells: Uint32Array;
  readonly sides: Uint8Array;
  readonly depths: Float64Array;
}

/**
 * Splits a map's table into one stand table per chunk that holds a marked cell.
 *
 * @param table The map's table, sorted by cell index.
 * @param chunkSize Chunk size in tiles.
 * @returns The tables, keyed by `cy * columns + cx` of the chunk grid, in chunk order.
 */
export function chunkStands(table: StandTable, chunkSize: number): Map<number, ChunkStand> {
  const span = chunkSize * COMPILED_COLLISION_CELLS_PER_TILE;
  const columns = Math.ceil(table.width / span);
  const grouped = new Map<number, { cells: number[]; sides: number[]; depths: number[] }>();
  for (let i = 0; i < table.keys.length; i += 1) {
    const key = table.keys[i] as number;
    const cx = key % table.width;
    const cy = Math.floor(key / table.width);
    const chunk = Math.floor(cy / span) * columns + Math.floor(cx / span);
    let group = grouped.get(chunk);
    if (group === undefined) {
      group = { cells: [], sides: [], depths: [] };
      grouped.set(chunk, group);
    }
    group.cells.push((cy % span) * span + (cx % span));
    group.sides.push(table.sides[i] as number);
    group.depths.push(table.depths[i] as number);
  }
  const out = new Map<number, ChunkStand>();
  for (const chunk of [...grouped.keys()].sort((a, b) => a - b)) {
    const group = grouped.get(chunk) as { cells: number[]; sides: number[]; depths: number[] };
    out.set(chunk, { cells: Uint32Array.from(group.cells), sides: Uint8Array.from(group.sides), depths: Float64Array.from(group.depths) });
  }
  return out;
}

/**
 * A chunk's stand table as plain JSON data.
 *
 * @param stand The table.
 * @returns The three arrays as base64.
 */
export function plainChunkStand(stand: ChunkStand): { readonly cells: string; readonly sides: string; readonly depths: string } {
  return { cells: encodeBase64(stand.cells), sides: encodeBase64(stand.sides), depths: encodeBase64(stand.depths) };
}

/**
 * Reads a stand table back from the bytes of its base64 text.
 *
 * @param text The base64 text.
 * @param width Bytes per element.
 * @param path Path for errors.
 * @returns A copy of the bytes as a view of the right alignment.
 * @throws Error when the length is not a whole number of elements.
 */
function bytesOf(text: string, width: number, path: string): DataView {
  const bytes = decodeBase64(text);
  if (bytes.length % width !== 0) {
    throw new Error(`${path} holds ${bytes.length} bytes, not a whole number of ${width}-byte values`);
  }
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/**
 * Reads and validates a chunk's stand table: the three arrays hold one value per cell, cells lie inside the chunk and
 * rise strictly, each side is `STAND_BEHIND` or `STAND_FRONT`, and each depth is finite.
 *
 * @param value The `stand` value of a chunk.
 * @param chunkSize Chunk size in tiles.
 * @returns The table.
 * @throws Error naming the path of the first fault.
 */
export function readChunkStand(value: unknown, chunkSize: number): ChunkStand {
  const record = asRecord(value, "stand");
  const cellView = bytesOf(asString(record["cells"], "stand.cells"), 4, "stand.cells");
  const sides = decodeBase64(asString(record["sides"], "stand.sides"));
  const depthView = bytesOf(asString(record["depths"], "stand.depths"), 8, "stand.depths");
  const count = cellView.byteLength / 4;
  if (sides.length !== count) {
    throw new Error(`stand.sides has ${sides.length} values, expected ${count}`);
  }
  if (depthView.byteLength / 8 !== count) {
    throw new Error(`stand.depths has ${depthView.byteLength / 8} values, expected ${count}`);
  }
  const span = chunkSize * COMPILED_COLLISION_CELLS_PER_TILE;
  const cells = new Uint32Array(count);
  const depths = new Float64Array(count);
  for (let i = 0; i < count; i += 1) {
    const cell = cellView.getUint32(i * 4, true);
    if (cell >= span * span) {
      throw new Error(`stand.cells[${i}] ${cell} is outside the chunk's ${span * span} cells`);
    }
    if (i > 0 && cell <= (cells[i - 1] as number)) {
      throw new Error(`stand.cells[${i}] ${cell} is not in rising order`);
    }
    const side = sides[i] as number;
    if (side !== STAND_BEHIND && side !== STAND_FRONT) {
      throw new Error(`stand.sides[${i}] ${side} is not a side`);
    }
    const depth = depthView.getFloat64(i * 8, true);
    if (!Number.isFinite(depth)) {
      throw new Error(`stand.depths[${i}] is not a finite depth`);
    }
    cells[i] = cell;
    depths[i] = depth;
  }
  return { cells, sides: Uint8Array.from(sides), depths };
}
